import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { Eyebrow, Page, PageHeader, Reveal } from "@/components/Reveal";
import { EMAIL, MAILTO, PHONE_DISPLAY, PHONE_TEL, REGIONS } from "@/constants/site";

const INFO = [
  {
    icon: Phone,
    label: "Phone",
    value: PHONE_DISPLAY,
    href: PHONE_TEL,
    testId: "contact-info-phone",
  },
  {
    icon: Mail,
    label: "Email",
    value: EMAIL,
    href: MAILTO,
    testId: "contact-info-email",
  },
  {
    icon: Clock,
    label: "Availability",
    value: "7 days a week during season",
    testId: "contact-info-hours",
  },
];

export default function Contact() {
  return (
    <Page>
      <PageHeader
        eyebrow="Contact Us"
        title={
          <>
            Let's get your crew <span className="italic text-[#86EFAC]">booked.</span>
          </>
        }
        copy="Call, email or send the form below. Tell us the crop, location, crew size and dates — we'll take it from there."
      />

      <section className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <Reveal>
              <Eyebrow>Reach Us Directly</Eyebrow>
            </Reveal>
            <div className="mt-10 space-y-4">
              {INFO.map((item, i) => (
                <Reveal key={item.label} delay={i * 0.1}>
                  <div
                    data-testid={item.testId}
                    className="flex items-center gap-5 rounded-2xl border border-white/10 bg-[#163B2C]/40 p-6"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#22C55E]/40 bg-[#0F291E]">
                      <item.icon className="h-5 w-5 text-[#22C55E]" />
                    </span>
                    <div>
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#86EFAC]/80">
                        {item.label}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          data-testid={`${item.testId}-link`}
                          className="mt-1 block text-base font-semibold text-[#F6F4EE] transition-colors hover:text-[#86EFAC] sm:text-lg"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="mt-1 text-base font-semibold text-[#F6F4EE] sm:text-lg">{item.value}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.3}>
              <div className="mt-10">
                <Eyebrow>Service Areas</Eyebrow>
                <div className="mt-5 flex flex-wrap gap-3">
                  {REGIONS.map((r) => (
                    <span
                      key={r.id}
                      data-testid={`contact-region-${r.id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#163B2C]/40 px-4 py-2 text-sm text-[#F6F4EE]/80"
                    >
                      <MapPin className="h-4 w-4 text-[#22C55E]" />
                      {r.name}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-white/10 bg-[#163B2C]/40 p-6 sm:p-10">
              <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Request labour</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/60">
                Fill this out and we'll get back to you as soon as possible — usually the same day.
              </p>
              <div className="mt-8">
                <ContactForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
